/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import { EventProxy } from "../../core/event-proxy";
import type { IHittable } from "../../core/ihittable";
import { Item } from "../../core/item";
import type { Player } from "../../core/player";
import type { HitObject } from "../../physics/hit-object";
import type { Table } from "../../table-model";
import type { KickerData } from "./kicker-data";
import { KickerHit } from "./kicker-hit";

/**
 * A kicker (hole, saucer or drain). It holds one ball until a kick releases it.
 */
export class Kicker extends Item<KickerData> implements IHittable {
  private hit?: KickerHit;

  public constructor(data: KickerData) {
    super(data);
  }

  public isCollidable(): boolean {
    return true;
  }

  public setupPlayer(player: Player, table: Table): void {
    const height =
      table.getSurfaceHeight(this.data.szSurface, this.data.center.x, this.data.center.y) *
      table.getScaleZ();

    // reduce the hit circle radius because only the inner circle of the kicker should start a hit event
    const radius = this.data.radius * (this.data.fallThrough ? 0.75 : 0.6);

    this.events = new EventProxy(this, "kicker", player.getEventSink());
    this.hit = new KickerHit(this.data, this.events, radius, height); // height of kicker hit cylinder
  }

  public getHitShapes(): HitObject[] {
    return [this.hit!];
  }

  public getKickerHit(): KickerHit {
    return this.hit!;
  }

  /** The role of this kicker in the rules. */
  public getRole() {
    return this.data.role;
  }

  /** The eject angle (degrees, 0 points up the table) and speed that the rules use when they give none. */
  public getEject(): { angle: number; speed: number } {
    return { angle: this.data.ejectAngle, speed: this.data.ejectSpeed };
  }

  /** The center of the kicker in table units. */
  public getCenter(): { x: number; y: number } {
    return { x: this.data.center.x, y: this.data.center.y };
  }

  /** The height of the playfield under the kicker. */
  public getSurface(): string | undefined {
    return this.data.szSurface;
  }

  /** True when a ball sits in the kicker. */
  public holdsBall(): boolean {
    return this.hit?.ball !== undefined;
  }
}
